import BaseModel from "lib/model";
import Replenishments from "collections/replenishments";
import Availability from "models/availability";
import Inventory from "collections/inventory";
import moment from "moment";

export default BaseModel.extend({
    props: {
        id: "objectId",
        active: "boolean",
        brand_id: "objectId",
        code: "string",
        display_name: "string",
        sort: "number",
    },
    session: {
        itemId: "objectId",
    },
    children: {
        availability: Availability,
    },
    collections: {
        replenishments: Replenishments,
    },
    fetchInventory(date, options = {}) {
        const inventory = new Inventory();
        inventory.parent = this;
        const data = {
            __date: date.valueOf(),
        };

        if (options.viewAs) {
            data.__company = options.viewAs._id;
        }

        inventory.fetch({
            data: data,
            success: function (collection, response, config) {
                if (options.success) {
                    options.success(collection, response, config);
                }
            },
            error: function (collection, response, config) {
                if (options.error) {
                    options.error(collection, response, config);
                }
            },
        });
    },
    /**
     * return delivery window matching period start
     * @param {moment} periodStart date selected from inventory by date modal
     * @param {object} activeWindow currently selected delivery window object
     * @returns {object} matching delivery window or null if none match
     */
    getDeliveryWindow(periodStart, activeWindow) {
        let deliveryWindow = null;
        periodStart = periodStart || moment.utc().startOf("day");
        const usingImmediate = activeWindow && activeWindow.adjustable_dates && !activeWindow.prebook;
        const usingPreBook = activeWindow && activeWindow.adjustable_dates && activeWindow.prebook;

        this.availability.delivery_windows.some((availableDeliveryWindow) => {
            if (!usingImmediate && availableDeliveryWindow.adjustable_dates && !availableDeliveryWindow.prebook) {
                // ignore immediate
                return false;
            }

            if (!usingPreBook && availableDeliveryWindow.adjustable_dates && availableDeliveryWindow.prebook) {
                return false;
            }

            if (
                !usingImmediate &&
                periodStart.isBetween(availableDeliveryWindow.start, availableDeliveryWindow.end, null, "[]")
            ) {
                deliveryWindow = availableDeliveryWindow;
                return true;
            }
            return false;
        });

        if (!deliveryWindow && usingImmediate) {
            deliveryWindow = this.getImmediateDeliveryWindow();
        }

        if (!deliveryWindow && usingPreBook) {
            deliveryWindow = this.getPreBookWindow();
        }

        if (!deliveryWindow) {
            deliveryWindow = this.availability.delivery_windows.at(0);
        }

        return deliveryWindow;
    },

    getPreBookWindow() {
        return this.availability.delivery_windows.getPreBookWindow();
    },

    getImmediateDeliveryWindow() {
        return this.availability.delivery_windows.getImmediateWindow();
    },

    /**
     * return object containing delivery window and start and end dates
     * @param {moment} periodStart date selected from inventory by date modal
     * @param {object} activeWindow currently selected delivery window object
     * @param {moment} requestedShipStart delivery date start
     * @param {moment} requestedShipEnd delivery date end
     * @returns {object} matching delivery window and start and end dates
     */
    getDeliveryWindowAndDeliveryDates(periodStart, activeWindow, requestedShipStart, requestedShipEnd) {
        const today = moment.utc().startOf("day");
        periodStart = periodStart || today.clone();
        const deliveryWindow = this.getDeliveryWindow(periodStart, activeWindow);
        if (!deliveryWindow) {
            return null;
        }

        /*
            Rules

            1. start date and end date must be within the delivery window
            2. start date and end date must be at least the minimum delivery window
            3. end date must be before the latest available date
        */

        let startDate = deliveryWindow.start;
        let endDate = deliveryWindow.end;

        if (deliveryWindow.adjustable_dates) {
            // take into account the requseted ship dates if the window is adjustable.
            startDate = requestedShipStart || deliveryWindow.start;
            endDate = requestedShipEnd || deliveryWindow.end;

            if (startDate.isBefore(deliveryWindow.start)) {
                startDate = deliveryWindow.start;
            }

            if (!endDate || endDate.diff(startDate, "days") < this.availability.minimum_delivery_window) {
                endDate = startDate.clone().add(this.availability.default_delivery_window, "days");
            }

            if (this.availability.latest_available_date && endDate.isAfter(this.availability.latest_available_date)) {
                endDate = this.availability.latest_available_date;
            }
        }

        return { deliveryWindow, startDate, endDate };
    },
});
